import type { ReactNode } from 'react';

import { type HelpPageContent, HelpSidebarDropdownTrigger, HelpSidebarTrigger } from 'components/help_sidebar';
import { CallToAction, type CallToActionProps } from 'components/layout/page/CallToAction';
import { ConditionalWrapper } from 'components/conditional_wrapper/ConditionalWrapper';
import { GMetrixTableExportTrigger } from 'components/GMetrixTable/components';
import type { GMetrixTableHandle } from 'components/GMetrixTable/GMetrixTable';
import { Breadcrumb, DropdownButton, DropdownItem } from 'react-bootstrap';
import type { BootstrapBreakpoint } from 'hooks/useWindowDimensions';
import { Link, useLocation } from 'react-router-dom';
import { useWindowDimensions } from 'hooks';

import 'components/layout/page/header/styles/PageHeader.scss';

export type BreadcrumbLink = {
    label: string;
    to: string;
}

export type PageHeaderProps = {
    actions?: CallToActionProps[];
    breakpoint?: BootstrapBreakpoint;
    cta?: CallToActionProps;
    current?: string;
    exportHandle?: GMetrixTableHandle | null;
    helpContent?: HelpPageContent;
    title: ReactNode | string;
    trail?: BreadcrumbLink[];
}

export const PageHeader = (props: PageHeaderProps) => {

    const { actions, breakpoint = 'md', cta, current, exportHandle, helpContent, title = '', trail } = props;

    const { isBelow: isMobile } = useWindowDimensions({ target: breakpoint });

    const location = useLocation();
    const backTo = (location.state as { from?: string } | null)?.from;

    // When the referring lookup passed its filtered URL as `state.from`, point the matching
    // breadcrumb (same pathname) back at it so the query-param filters survive the round-trip.
    const resolveTrailTo = (to: string) => (backTo && backTo.split('?')[0] === to.split('?')[0] ? backTo : to);

    return (
        <div className={'page-header d-flex flex-column gap-3'}>

            <ConditionalWrapper
                wrapper={children => (
                    <div className={'d-flex flex-column'}>
                        {children}
                    </div>
                )}
                condition={!!trail?.length && !!title}
            >
                {trail?.length && (
                    <Breadcrumb>
                        {trail.map(({ label, to }, key) => (
                            <Breadcrumb.Item key={key} linkAs={Link} linkProps={{ state: backTo ? { from: backTo } : undefined, to: resolveTrailTo(to) }}>
                                {label}
                            </Breadcrumb.Item>
                        ))}

                        <Breadcrumb.Item active>
                            {current || title}
                        </Breadcrumb.Item>
                    </Breadcrumb>
                )}

                <div className={'d-flex flex-column flex-wrap flex-md-row align-items-md-end justify-content-between gap-3'}>
                    <h1 className={'m-0 text-strong'}>
                        {title}
                    </h1>

                    <div className={`end-content d-flex align-content-center gap-1`}>

                        {!isMobile && !!helpContent && <HelpSidebarTrigger className={'order-0'} content={helpContent}/>}

                        {/* XLSX / CSV export button */}
                        {!isMobile && !!exportHandle && <GMetrixTableExportTrigger handle={exportHandle} type={'button'} />}

                        {/* Secondary actions */}
                        {!isMobile && actions?.length && actions.map((action, key) => (
                            <CallToAction key={key} {...action} />
                        ))}

                        {/* Primary CTA */}
                        {!!cta && <CallToAction className={'order-1 order-md-2'} {...cta} />}

                        {isMobile && !!helpContent && (
                            <>
                                {(!!actions?.length || !!exportHandle) ?
                                    (
                                        <DropdownButton
                                            className={'actions-dropdown order-2'}
                                            title={<i className={'fa-regular fa-ellipsis-vertical'} />}
                                            variant={'outline-primary'}
                                        >
                                            {actions?.length && actions.map(({ disabled, label, onClick, to }, key) => (
                                                <DropdownItem as={to?.length && Link} disabled={disabled} key={key} onClick={onClick} to={to}>
                                                    {label}
                                                </DropdownItem>
                                            ))}

                                            {!!exportHandle && <GMetrixTableExportTrigger handle={exportHandle} type={'dropdown'} />}

                                            {!!helpContent && <HelpSidebarDropdownTrigger content={helpContent} />}

                                        </DropdownButton>
                                    )
                                    :
                                    (
                                        <HelpSidebarTrigger className={'order-2'} content={helpContent}/>
                                    )
                                }
                            </>
                        )
                        }

                    </div>
                </div>
            </ConditionalWrapper>

            <hr className={'mt-0'}/>

        </div>
    );

};